import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  areStationsDragReorderedAtom,
  buildRideMetadataAfterStationStructureDrySave,
  bulkUpdateRidePassengersStations,
  castErrorResult,
  ConfirmResult,
  createErrorResult,
  createErrorResultTranslated,
  createSuccessResult,
  DEFAULT_PASSENGER_SEATS_COUNT,
  DialogType,
  DirectionEnum,
  dropdownPassengersWithStationsAtom,
  dropdownStationListAtom,
  errors,
  generateStationRouteWaypointSecondaryId,
  getFirstAddedStationArrivalTime,
  getLastAddedStationArrivalTime,
  isTranslatableItem,
  lastSelectedRebuildTypeAtom,
  Log,
  mergeInsertedStationWithoutPassengers,
  mergeStationsSecondaryIds,
  normalizeRideMetadata,
  rideManualDateChangeInfoAtom,
  RidesApi,
  RideStatusEnum,
  sortStationsByPassengers,
  StationApi,
  useDialogs,
  warnAndToastInfoItemsAsDetails,
  warnAndToastSimpleResult,
  type IDropdownItemStations,
  type IPassengerInfo,
  type IRideDto,
  type IRideManualChangeStartEndInfo,
  type IRideMetadata,
  type IRideStationsBulkUpdateParams,
  type IRideStationsBulkUpdateRecountData,
  type IStationInfo,
  type RideUpdateModel,
  type SimpleResult,
} from "@ryde/shared";

import { useDropdownSourceContext } from "../../contexts";
import { useModalConfirm } from "../../hooks-singletons";
import { useRideDtoActions } from "./use-ride-dto-actions";
import { useRideEdit } from "./use-ride-edit";
import { fetchPassengersWithStationsDeduped } from "./use-ride-station-actions.utils";
import { useRideValidators } from "./use-ride-validators";

export const useRideStationActions = () => {
  const { t } = useTranslation();
  const { updateEditingRideDto } = useRideDtoActions();
  const { validateStationInfo } = useRideValidators();
  const [passengerDropdownItems, setPassengerDropdownItems] = useRecoilState(
    dropdownPassengersWithStationsAtom,
  );
  const setStationsDropdownItems = useSetRecoilState(dropdownStationListAtom);
  const setStationsAreDragReordered = useSetRecoilState(
    areStationsDragReorderedAtom,
  );
  const setLastSelectedRebuildType = useSetRecoilState(
    lastSelectedRebuildTypeAtom,
  );
  const manualRideDateChangeInfo = useRecoilValue(rideManualDateChangeInfoAtom);
  const { confirmDialogResult } = useModalConfirm();
  const { openAsyncDialog } = useDialogs();
  const { getDropdownItems } = useDropdownSourceContext();
  const { saveRide } = useRideEdit();

  const mergeMissingStations = useEvent(
    async (
      rideDto: Partial<IRideDto>,
      stationFromId: number | undefined,
      stationToId: number | undefined,
    ): Promise<SimpleResult<IStationInfo[]>> => {
      const stationsInfoSnapshot = [...(rideDto?.metadata?.stationsInfo || [])];
      const prevStationsIds = stationsInfoSnapshot.map(x => x.id);
      const prevStationsIdsSet = new Set(prevStationsIds);

      const addedStations: IStationInfo[] = [];

      if (stationFromId && !prevStationsIdsSet.has(stationFromId)) {
        const stationInfoFrom =
          await StationApi.fetchStationInfo(stationFromId);

        if (!stationInfoFrom?.length || !stationInfoFrom) {
          const errorTranslated = t("errors.invalidStationFetched", {
            stationToId,
          });
          return createErrorResultTranslated(errorTranslated);
        }

        const addedStation = stationInfoFrom[0];

        const validateResult = validateStationInfo(addedStation);

        if (!validateResult.success) {
          warnAndToastInfoItemsAsDetails({
            items: validateResult.data,
            tFunction: t,
            headerTranslationKey: errors.ride.stationHasMissingData,
          });
        }

        addedStation.secondaryId = generateStationRouteWaypointSecondaryId();
        addedStations.push(addedStation);
      }

      if (stationToId && !prevStationsIdsSet.has(stationToId)) {
        const stationInfoTo = await StationApi.fetchStationInfo(stationToId);

        if (!stationInfoTo?.length || !stationInfoTo) {
          const errorTranslated = t("errors.invalidStationFetched", {
            stationToId,
          });
          return createErrorResultTranslated(errorTranslated);
        }

        addedStations.push(stationInfoTo[0]);
      }

      const combinedStationsInfos = mergeInsertedStationWithoutPassengers({
        rideStations: stationsInfoSnapshot,
        stationsToAdd: addedStations,
      });

      return createSuccessResult(combinedStationsInfos);
    },
  );

  const rideAddPassengerAction = useEvent(
    async ({
      rideDto,
      passengerAddId,
      stationFromId,
      stationToId,
      seats = DEFAULT_PASSENGER_SEATS_COUNT,
    }: {
      rideDto: Partial<IRideDto>;
      passengerAddId: number | undefined;
      stationFromId: number | undefined;
      stationToId: number | undefined;
      seats?: number;
    }): Promise<SimpleResult<RideUpdateModel | void>> => {
      if (!passengerAddId || !+passengerAddId) {
        return createErrorResult(errors.ride.choosePassenger);
      }

      // Reset custom rebuild type when adding passenger (changes station structure)
      setLastSelectedRebuildType(null);

      if (!stationFromId) {
        return createErrorResult(errors.ride.chooseStationFrom);
      }

      if (!stationToId) {
        return createErrorResult(errors.ride.chooseStationTo);
      }

      if (stationToId === stationFromId) {
        return createErrorResult(errors.ride.chooseDifferentStationToFrom);
      }

      const direction = rideDto.direction;
      Log.debug(
        `Adding passenger to ride ${
          rideDto.id || "new ride"
        },passenger id: ${passengerAddId}, direction ${direction}`,
      );

      const passengerInfo = await RidesApi.fetchListPassengersInfos([
        passengerAddId,
      ]);

      if (!passengerInfo?.length) {
        return createErrorResult(errors.ride.invalidPassengersInfoFromAPI);
      }

      const combinedStationInfosResult = await mergeMissingStations(
        rideDto,
        stationFromId,
        stationToId,
      );

      if (!combinedStationInfosResult.success) {
        return castErrorResult(combinedStationInfosResult);
      }

      const isFirstPassenger = !rideDto?.metadata?.passengersInfo?.length;

      const forcedRideTiming: IRideManualChangeStartEndInfo | undefined =
        isFirstPassenger && rideDto.rideStart
          ? manualRideDateChangeInfo || {
              fieldChanged: "rideStart" as const,
              value: rideDto.rideStart,
            }
          : undefined;

      const newPassenger: IPassengerInfo = {
        ...passengerInfo[0],
        from: stationFromId,
        to: stationToId,
        seats: Number(seats) || 1,
      };

      const metadataToUpdate: IRideMetadata = { ...(rideDto?.metadata || {}) };
      const passInfosToUpdate = [
        ...(rideDto?.metadata?.passengersInfo || []),
        newPassenger,
      ];

      const stationsInfoSorted = sortStationsByPassengers({
        rideDto,
        stationsInfos: combinedStationInfosResult.data,
        passengersInfos: passInfosToUpdate,
      });

      Log.debug(
        "Pushing passenger to ride metadata ",
        newPassenger,
        passInfosToUpdate,
      );
      metadataToUpdate.passengersInfo = passInfosToUpdate;
      metadataToUpdate.stationsInfo = stationsInfoSorted;
      const metadataRecounted = normalizeRideMetadata(metadataToUpdate, {
        direction: rideDto?.direction || DirectionEnum.PickUp,
      });

      Log.debug(
        "Composed metadata after add passenger for checking dry save ride:",
        metadataRecounted,
      );

      const rideCheckedResult = await saveRide?.({
        rideDto,
        sendToSupplier: false,
        dryRun: true,
        metadataDryRun: metadataRecounted,
        forcedRideTiming,
        skipChangedValuesConfirmation: !rideDto.id,
        isReturnDtoForGrid: false,
      });

      if (!rideCheckedResult.success) {
        return rideCheckedResult;
      }

      const rideChecked = rideCheckedResult.data;

      if (!rideChecked) {
        return createErrorResult(errors.ride.invalidRideIDBadAPIResponse);
      }

      const confirmationAlert = rideChecked.confirmationAlert;

      if (
        confirmationAlert !== undefined &&
        rideChecked.status !== RideStatusEnum.Draft
      ) {
        const confirmationResult = await openAsyncDialog({
          dialogName: DialogType.RideConfirm,
          dialogProps: {
            title: t("general.CONFIRM"),
            message: t(confirmationAlert.message),
          },
        });

        if (!confirmationResult.success) {
          return createErrorResult("errors.passengerNotAddedCanceledByUser");
        }
      }

      Log.debug("Dry checked ride from api ", rideChecked);

      const { confirmationAlert: _confirmationAlert, ...rideToApply } =
        rideChecked;
      updateEditingRideDto({
        ...rideToApply,
        metadata: buildRideMetadataAfterStationStructureDrySave({
          previousMetadata: rideDto.metadata,
          drySaveMetadata: rideToApply.metadata,
        }),
      });
      setStationsAreDragReordered(false);
      return createSuccessResult();
    },
  );

  const rideAddStationWithoutPassengersAction = useEvent(
    async ({
      rideDto,
      stationAddId,
      isRideWithDuplicatedStationsAllowed,
    }: {
      rideDto: Partial<IRideDto>;
      stationAddId: number | undefined;
      isRideWithDuplicatedStationsAllowed?: boolean;
    }): Promise<SimpleResult<RideUpdateModel | void>> => {
      if (!stationAddId) {
        return createErrorResult(errors.ride.chooseStationToAdd);
      }

      setLastSelectedRebuildType(null);

      const isFirstStation = !rideDto?.metadata?.stationsInfo?.length;

      const forcedRideTiming: IRideManualChangeStartEndInfo | undefined =
        isFirstStation && rideDto.rideStart
          ? manualRideDateChangeInfo || {
              fieldChanged: "rideStart" as const,
              value: rideDto.rideStart as TDateISO,
            }
          : undefined;

      const stationsInfoSnapshot = [...(rideDto?.metadata?.stationsInfo || [])];
      const prevStationsIds = stationsInfoSnapshot.map(x => x.id);
      const prevStationsIdsSet = new Set(prevStationsIds);

      const addedStations: IStationInfo[] = [];

      if (!prevStationsIdsSet.has(stationAddId)) {
        const stationInfoFromRide =
          await StationApi.fetchStationInfo(stationAddId);

        if (!stationInfoFromRide?.length || !stationInfoFromRide) {
          const warningMessage = t(errors.ride.invalidStationFetched, {
            stationId: stationAddId,
          });
          return createErrorResultTranslated(warningMessage);
        }

        const stationInfoFrom: IStationInfo = stationInfoFromRide[0];

        const validateResult = validateStationInfo(stationInfoFrom);

        if (!validateResult.success) {
          warnAndToastInfoItemsAsDetails({
            items: validateResult.data,
            tFunction: t,
            headerTranslationKey: errors.ride.stationHasMissingData,
          });
        }

        if (!prevStationsIds?.length) {
          stationInfoFrom.arrivalTime =
            getFirstAddedStationArrivalTime(rideDto);
        } else {
          stationInfoFrom.arrivalTime = getLastAddedStationArrivalTime(rideDto);
        }

        stationInfoFrom.secondaryId = generateStationRouteWaypointSecondaryId();

        addedStations.push(stationInfoFrom);
      } else if (isRideWithDuplicatedStationsAllowed) {
        const existingSameIdStation = stationsInfoSnapshot.find(
          x => x.id === stationAddId,
        );

        if (existingSameIdStation) {
          const stationSameIdToAdd: IStationInfo = {
            ...existingSameIdStation,
            secondaryId: generateStationRouteWaypointSecondaryId(),
          };

          if (!prevStationsIds?.length) {
            stationSameIdToAdd.arrivalTime =
              getFirstAddedStationArrivalTime(rideDto);
          } else {
            stationSameIdToAdd.arrivalTime =
              getLastAddedStationArrivalTime(rideDto);
          }

          addedStations.push(stationSameIdToAdd);
        }
      }

      const combinedStationsInfos = mergeInsertedStationWithoutPassengers({
        rideStations: stationsInfoSnapshot,
        stationsToAdd: addedStations,
      });
      Log.debug(
        "Combined stations info after adding passenger's station ",
        combinedStationsInfos,
      );
      const metadataToUpdate = { ...(rideDto?.metadata || {}) };
      metadataToUpdate.stationsInfo = combinedStationsInfos;
      const metadataRecounted = normalizeRideMetadata(metadataToUpdate, {
        direction: rideDto?.direction || DirectionEnum.PickUp,
      });

      Log.debug(
        "Composed metadata after add passenger for checking dry save ride:",
        metadataRecounted,
      );

      const rideCheckedResult = await saveRide?.({
        rideDto,
        sendToSupplier: false,
        dryRun: true,
        metadataDryRun: metadataRecounted,
        forcedRideTiming,
        skipChangedValuesConfirmation: !rideDto.id,
        isReturnDtoForGrid: false,
      });

      if (!rideCheckedResult.success) {
        return rideCheckedResult;
      }

      const rideChecked = rideCheckedResult.data;

      if (!rideChecked) {
        return createErrorResult(errors.ride.invalidRideIDBadAPIResponse);
      }

      Log.debug("Dry checked ride from api ", rideChecked);

      const metadataAfterDrySave =
        buildRideMetadataAfterStationStructureDrySave({
          previousMetadata: rideDto.metadata,
          drySaveMetadata: rideChecked.metadata,
        });
      const stationsInfoWithSecondaryIds = mergeStationsSecondaryIds(
        combinedStationsInfos,
        metadataAfterDrySave.stationsInfo ?? [],
      );

      updateEditingRideDto({
        ...rideChecked,
        metadata: {
          ...metadataAfterDrySave,
          stationsInfo: stationsInfoWithSecondaryIds,
        },
      });
      setStationsAreDragReordered(false);
      return createSuccessResult();
    },
  );

  const bulkUpdateRideStations = useEvent(
    async (
      criteriaUpdate: IRideStationsBulkUpdateParams,
    ): Promise<SimpleResult<void | RideUpdateModel>> => {
      const { passengerIds, stationFromId, stationToId, rideDto } =
        criteriaUpdate;

      if (!passengerIds?.length) {
        return createErrorResult(errors.ride.noPassengersSelectedForUpdate);
      }

      if (!Number(stationFromId) && !Number(stationToId)) {
        return createErrorResult("errors.invalidStationsSelectedForUpdate");
      }

      // Reset custom rebuild type when bulk updating stations (changes station structure)
      setLastSelectedRebuildType(null);

      const confirmationResult = await confirmDialogResult({
        title: t("ride.PLEASE_APPROVE"),
        message: t(`ride.PASSENGERS_WILL_BE_UPDATED`, {
          count: passengerIds.length,
        }),
      });

      if (confirmationResult !== ConfirmResult.OK) {
        return createErrorResult("errors.bulkUpdateCanceledByUser");
      }

      const combinedStationInfosResult = await mergeMissingStations(
        rideDto,
        stationFromId,
        stationToId,
      );

      if (!combinedStationInfosResult.success) {
        return castErrorResult(combinedStationInfosResult);
      }

      const recountData: IRideStationsBulkUpdateRecountData = {
        stationsInfos: combinedStationInfosResult.data || [],
      };

      const metadataRecounted = bulkUpdateRidePassengersStations(
        criteriaUpdate,
        recountData,
      );

      Log.debug(
        "Composed metadata after bulk update stations for checking dry save ride:",
        metadataRecounted,
      );

      const rideCheckedResult = await saveRide?.({
        rideDto,
        sendToSupplier: false,
        dryRun: true,
        metadataDryRun: metadataRecounted,
        skipChangedValuesConfirmation: !rideDto.id,
        isReturnDtoForGrid: false,
      });

      if (!rideCheckedResult.success) {
        return rideCheckedResult;
      }

      const rideChecked = rideCheckedResult.data;

      if (!rideChecked) {
        return createErrorResult(errors.ride.invalidRideIDBadAPIResponse);
      }

      Log.debug("Dry checked ride from api ", rideChecked);

      const { confirmationAlert: _bulkConfirmationAlert, ...rideToApply } =
        rideChecked;
      updateEditingRideDto({
        ...rideToApply,
        metadata: buildRideMetadataAfterStationStructureDrySave({
          previousMetadata: rideDto.metadata,
          drySaveMetadata: rideToApply.metadata,
        }),
      });
      Log.debug("Station added successfully"); // TODO: add translation solution/key? add as toast?
      return createSuccessResult();
    },
  );

  /** Resolves with the list it stored, which the caller's own render does not hold yet. */
  const fetchStations = useEvent(
    async (force = false): Promise<IDropdownItemStations[]> => {
      const stations: IDropdownItemStations[] =
        (await getDropdownItems?.("station", force)) || [];
      setStationsDropdownItems(stations);

      return stations;
    },
  );

  const fetchPassengers = useEvent(async (force = false): Promise<void> => {
    if (passengerDropdownItems !== undefined && !force) {
      return;
    }

    // The guard above is per hook instance, so it cannot stop two instances mounting together from
    // both fetching - concurrent callers share one request here instead.
    const passResult = await fetchPassengersWithStationsDeduped(force);

    if (!passResult?.success) {
      if (isTranslatableItem(passResult?.error)) {
        warnAndToastSimpleResult(passResult, t);
      } else {
        Log.warnAndToast(errors.ride.invalidPassengersInfoFromAPI);
      }

      return;
    }

    setPassengerDropdownItems(passResult?.data || []);
  });

  const fetchDropdownPassengersAndStations = useEvent(async () => {
    await Promise.allSettled([fetchStations(), fetchPassengers()]);
  });

  return {
    rideAddPassengerAction,
    rideAddStationWithoutPassengersAction,
    fetchStations,
    bulkUpdateRideStations,
    fetchDropdownPassengersAndStations,
  };
};
